import { v4 as uuidv4 } from "uuid";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { URL_CANOPUS_DOCUMENT, URL_GEN_SEARCH } from "../constants";

import {
  GenerativeSearchReq,
  GenerativeSearchResponse,
  WMMSBusinessUnit,
  AppState,
  logRequest,
  UserDetails,
} from "../../types";
import {
  logGenAIQueryResponse,
  logResponseCopied,
  logSourceClicked,
  logTicketRaised,
} from "./logthunks";
import { httpClient } from "../httpclient";

interface GenaiReq {
  payload: GenerativeSearchReq;
  logAnswerPayload: logRequest;
  headers: any;
}
export const fetchGenAIQueryResponse = createAsyncThunk(
  "genaiQuerySearch/fetchResponse",
  async (
    // { payload, logAnswerPayload, headers }: GenaiReq,
    { payload, headers }: GenaiReq,
    // thunkAPI
  ): Promise<GenerativeSearchResponse> => {
    try {
      const response = await httpClient.post<GenerativeSearchResponse>(
        URL_GEN_SEARCH,
        payload,
        {
          headers: headers
        }
      );
      // const logPayload = { ...logAnswerPayload, response: response.data.response.Answer };
      // thunkAPI.dispatch(logGenAIQueryResponse(logPayload));
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch GenAI response to query.");
    }
  }
);

interface CanopusDocReq {
  payload: string;
  headers: any;
}
interface CanopusDocResp {
  uid: string;
  blob: Blob;
}
export const fetchReferenceDocument = createAsyncThunk(
  "sourceReferences/fetchDocument",
  async ({ payload, headers }: CanopusDocReq): Promise<CanopusDocResp> => {
    try {
      const response = await httpClient.get<any>(
        `${URL_CANOPUS_DOCUMENT}/${payload}/1.0/content`,
        {
          headers: headers
        }
      );
      const pdfData = new Blob([response.data], { type: "application/pdf" });
      return { uid: payload, blob: pdfData };
    } catch (error) {
      throw new Error("Failed to fetch source document.");
    }
  }
);

const initialState = {
  domain: "eWMMS",
  headers: {},
  authSign:{}
} as AppState;

const appSlice = createSlice({
  name: "ewmms",
  initialState,
  reducers: {
    setAuthSign(state, action) {
      state.authSign = action.payload;
    },
    setHeaders(state, action) {
      state.headers = action.payload;
    },
    createNewSession(state) {
      state.sessionId = uuidv4();
      state.genAIWaiting = false;
    },
    loadUser(state, action: {
      payload: UserDetails;
      type: string;
    }) {
      state.userDetails = {
        userId: action.payload.userId,
        userName: action.payload.userName,
        businessUnit: action.payload.businessUnit || WMMSBusinessUnit.ANY,
        state: action.payload.state
      };
    },
    setAnswerSpinner(state, action) {
      state.genAIWaiting = action.payload.loading;
      state.currentQuestion = action.payload.searchQuestion;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGenAIQueryResponse.fulfilled, (state, action) => {
        state.genAIResponse = action.payload;
        state.genAIWaiting = false;
      })
      .addCase(fetchReferenceDocument.fulfilled, (state, action) => {
        if (state.documentPaths) {
          state.documentPaths.set(
            action.payload.uid,
            URL.createObjectURL(action.payload.blob)
          );
        } else {
          state.documentPaths = new Map([
            [action.payload.uid, URL.createObjectURL(action.payload.blob)],
          ]);
        }
      })
      .addCase(fetchGenAIQueryResponse.rejected, (state) => {
        state.genAIResponse = {
          response: {
            Answer: "Something went wrong with the application. Please try again. ",
            Alt_Questions: ["How to use e-WMMS?"],
          },
          source_documents: [],
        };
        state.genAIWaiting = false;
        state.currentQuestion = "";
        return state;
      })
      .addCase(logGenAIQueryResponse.fulfilled, (state, action) => {
        state.querySearchLog = {
          ...action.payload,
          isSourceClicked: false,
          isResponseCopied: false,
          isTicketRaised: false,
        };
      })
      .addCase(logGenAIQueryResponse.rejected, (state) => {
        state.currentQuestion = "";
        return state;
      })
      .addCase(logSourceClicked.fulfilled, (state) => {
        state.querySearchLog.isSourceClicked = true;
      })
      .addCase(logResponseCopied.fulfilled, (state) => {
        state.querySearchLog.isResponseCopied = true;
      })
      .addCase(logTicketRaised.fulfilled, (state) => {
        state.querySearchLog.isTicketRaised = true;
      });
  },
});

export const {setHeaders,setAuthSign ,createNewSession, loadUser, setAnswerSpinner } = appSlice.actions;
export default appSlice;
